import $ from 'jquery';
// @ts-ignore
import Turbolinks from 'javascripts/turbolinks';
import { getDrawerStacker } from 'javascripts/drawer_stacker_instance';
import { unmountComponents } from 'javascripts/react_mount';
import ReactiveRegister from 'javascripts/reactive_register';
import type DrawerStacker from './drawer_stacker';
import { showWarningMessage } from 'javascripts/messages';
import drawerCapableClasses from 'javascripts/constants/drawerCapableClasses.json';
import { type RecordReferenceStruct } from './shared-types';
import { pathForRecordRef } from './url_helpers';
import Router, { makeUrl } from '../components/whiteboards/Router';
import AssistantContext, { getReferenceNumFromUrl } from './assistant_context';
import popoverStacker from './popoverStacker';

type HistoryItem = {
  url: string;
  title?: string;
  options?: ShowUrlOptions;
};

export type DrawerOptions = {
  drawerSelector?: string;
  selector?: string;
  dataAttr?: string;
  eventPrefix?: string;
  keepExistingContent?: boolean;
  routing?: boolean;
  customDrawerId?: string;
};

type ShowUrlOptions = {
  keepExistingContent?: boolean;
  headers?: JQuery.UrlAjaxSettings['headers'];
  replace?: boolean;
  data?: Record<string, object>;
  usePost?: boolean;
  fromAssistant?: boolean;
};

interface AhaDrawer extends DrawerOptions {}

const ANCHOR_TABS = [
  ['comment_', 'comments'],
  ['integration_measurement_', 'engagement'],
  ['requirement_', 'requirements'],
  ['task_', 'tasks'],
];

export const drawerable = recordType => {
  return drawerCapableClasses.includes(recordType);
};

type RouteInfo = {
  drawerUrl: string | undefined;
};

const parseRouteInfo = (params: URLSearchParams): RouteInfo => {
  const drawerUrl = params.get('d') ?? undefined;
  return { drawerUrl };
};

class AhaDrawer {
  drawerSelector: string;
  selector: string;
  dataAttr: string;
  eventPrefix: string;
  customDrawerId?: string;

  stacker: DrawerStacker;
  options: DrawerOptions;
  history: Array<HistoryItem> = [];
  clickedField?: HTMLElement;
  closeButtonTitle: string;
  reactiveDestroyId: string | undefined;
  loading: boolean = false;
  scopedEventTypes: Record<string, string>;

  private readonly router: Router<RouteInfo>;

  static createCustomDrawer(opts = {}) {
    const stacker = getDrawerStacker();
    return stacker?.createCustomDrawer(opts);
  }

  constructor(stacker: DrawerStacker, options: DrawerOptions = {}) {
    this.stacker = stacker;
    this.drawerSelector = options.drawerSelector || '.drawer';
    this.selector = options.selector || '[data-drawer-url]';
    this.dataAttr = options.dataAttr || 'data-drawer-url';
    this.eventPrefix = options.eventPrefix || 'aha-drawer';
    this.options = options;

    if (
      window.featureFlags?.A_18022_add_popover_to_legacy_elements &&
      window.featureFlags?.ACT_1949_multiple_drawers
    ) {
      this.customDrawerId = options.customDrawerId;
      // If a customDrawerId is provided, we append it to the event prefix, otherwise we just use the default event prefix.
      // This allows us to have multiple instances of the same drawer type (e.g., multiple side drawers) without event conflicts while still allowing the default drawer to use the same event prefix (and not breaking any existing functionality).
      this.eventPrefix = `${this.eventPrefix}${
        this.customDrawerId ? `-${this.customDrawerId}` : ''
      }`;
    }

    this.scopedEventTypes = {
      click: `click.${this.eventPrefix}-open`,
      dblclick: `dblclick.${this.eventPrefix}-dblclick`,
      backClick: `click.${this.eventPrefix}-back`,
      closeClick: `click.${this.eventPrefix}-close`,
    };

    this.bindEventHandlers();

    this.router = new Router(
      (_, params) => parseRouteInfo(params),
      ({ drawerUrl }) => {
        this._showUrl(drawerUrl, {});
      },
      {
        enabled: () =>
          !!(
            this.options.routing && window.featureFlags?.awt_150_drawer_routing
          ),
        replace: true,
      }
    );
  }

  // Gets the full selector for the drawer, which includes the customDrawerId if it exists. Using the `drawerSelector` is not unique enough and may return multiple elements (ex. multiple side drawers).
  get uniqueDrawerSelector() {
    return this.customDrawerId
      ? `${this.drawerSelector}[data-custom-drawer-id="${this.customDrawerId}"]`
      : `${this.drawerSelector}:not([data-custom-drawer-id])`;
  }

  $el() {
    if (
      window.featureFlags?.A_18022_add_popover_to_legacy_elements &&
      window.featureFlags?.ACT_1949_multiple_drawers
    ) {
      return $(this.uniqueDrawerSelector);
    } else {
      return $(this.drawerSelector);
    }
  }

  get drawerType() {
    return this.$el()[0].classList.contains('bottom-drawer')
      ? 'bottom'
      : 'side';
  }

  $spinner() {
    return this?.$el()?.find('.aha-spinner');
  }

  scrollable(url: string) {
    return (
      url.indexOf('/personas/') !== -1 ||
      url.indexOf('/competitors/') !== -1 ||
      url.indexOf('/creative_briefs/') !== -1
    );
  }

  // NOTE: We can't use class properties for the event listeners,
  // since it prevents child classes from overriding them.
  clickHandler(event: JQuery.ClickEvent) {
    if (
      this.maybeStopLink(event) ||
      $(event.target).closest('a[target="_blank"]').length > 0
    ) {
      return;
    }

    // Suppress drawer open for any cmd/ctrl click, which should open a new tab (or in some
    // cases select the element, e.g. Draggable, Sortable)
    if (event.metaKey || event.ctrlKey) {
      return;
    }

    const url = this.dataUrlFromEvent(event);

    if (url && this.scrollable(url)) {
      this.clickedField = event.target.closest(
        'section.draggable-layout-field'
      );
    }

    const opts: ShowUrlOptions = {};
    if ($(event.currentTarget).hasClass('assistant-reference')) {
      opts.fromAssistant = true;
    }

    this.showUrl(url);

    event.preventDefault();
    event.stopPropagation();
  }

  doubleClickHandler(event: JQuery.DoubleClickEvent) {
    if (this.maybeStopLink(event)) {
      return;
    }
    const url = this.dataUrlFromEvent(event);
    if (url && !url.match(/\.\w+$/)) {
      // Don't support dbclick if the url contains format type like .drawer .details as these urls wont work by themselves
      Turbolinks.visit(url);
    }
  }

  closeHandler(event: JQuery.ClickEvent) {
    this.showUrl(undefined);
    event.preventDefault();
  }

  bindEventHandlers() {
    // To support multi-drawer functionality, we need to bind the close button and back button click handlers
    // to the unique drawer selector.
    const instanceSelector =
      window.featureFlags?.A_18022_add_popover_to_legacy_elements &&
      window.featureFlags?.ACT_1949_multiple_drawers
        ? this.uniqueDrawerSelector
        : this.drawerSelector;

    $(document).rebind(
      this.scopedEventTypes.closeClick,
      `${instanceSelector} .drawer-close-button`,
      this.closeHandler.bind(this)
    );

    // Published pages should not be able to be interacted with, except for being able to
    // close the drawer when drilling into charts
    if (window.isPublishing) return;

    $(document).rebind(
      this.scopedEventTypes.click,
      this.selector,
      this.clickHandler.bind(this)
    );

    $(document).rebind(
      this.scopedEventTypes.dblclick,
      this.selector,
      this.doubleClickHandler.bind(this)
    );

    $(document).rebind(
      this.scopedEventTypes.backClick,
      `${instanceSelector} .back-button`,
      this.back.bind(this)
    );
  }

  back = (event: JQuery.ClickEvent) => {
    this.history.pop(); // pop off the current page
    const historyItem = this.history.pop(); // pop off the last page for use with showUrl
    if (historyItem) {
      const { url, options } = historyItem;
      this.showUrl(url, options);
    }
  };

  load($target: JQuery) {
    const url = this.urlForElement($target);
    this.showUrl(url);
  }

  urlForElement(element: JQuery) {
    if (element.hasClass('drawer-url')) {
      return element.attr('href');
    } else {
      return (
        element.attr(this.dataAttr) ||
        element.parents(this.selector).attr(this.dataAttr)
      );
    }
  }

  dataUrlFromEvent(event: JQuery.ClickEvent | JQuery.DoubleClickEvent) {
    const $currentTarget = $(event.currentTarget);
    const $target = $(event.target);
    return this.urlForElement($currentTarget) || this.urlForElement($target);
  }

  currentItem() {
    return this.history[this.history.length - 1];
  }

  activeTabForUrl(url: string): string | undefined {
    // Find the active tab
    const parsed = new URL(url, window.location.href);

    let activeTab = ANCHOR_TABS.find(
      ([prefix, tab]) => parsed.hash.indexOf(prefix) === 1
    )?.[1];

    // Special case -- idea comments are on the idea_comments tab.
    if (activeTab === 'comments' && url.startsWith('/ideas')) {
      activeTab = 'idea_comments';
    }

    return activeTab;
  }

  currentUrl() {
    const item = this.currentItem();

    if (item) {
      return item.url;
    }
  }

  reloadCurrentUrl(options: ShowUrlOptions = {}) {
    const url = this.currentUrl();

    if (url) {
      return this.showUrl(url, { replace: true, ...options });
    }
  }

  // Similar to history.replaceState, but for drawer history
  replaceUrl(url: URL | string) {
    const currentItem = this.currentItem();

    if (currentItem) {
      currentItem.url = String(url);
    }
  }

  openRecord(recordRef: RecordReferenceStruct) {
    const path = pathForRecordRef(recordRef);

    if (path) {
      this.showUrl(path);
    }
  }

  private getWindowUrl(drawerUrl: string | undefined) {
    return makeUrl({ d: drawerUrl });
  }

  public showUrl(url: string | undefined, options: ShowUrlOptions = {}) {
    let drawer: AhaDrawer = this;

    if (
      window.featureFlags?.A_18022_add_popover_to_legacy_elements &&
      window.featureFlags?.ACT_1949_multiple_drawers
    ) {
      const drawerElem = drawer.$el()[0];
      const drawerHasFullscreen = Boolean(
        drawerElem.matches('.aha-fullscreen:popover-open') ||
          drawerElem.querySelector(':scope .aha-fullscreen:popover-open')
      );
      if (drawerHasFullscreen && url) {
        // If we are currently in a drawer that is fullscreened or has a fullscreened element, we need to create a new drawer.
        const newDrawer = AhaDrawer.createCustomDrawer(this.options);
        if (newDrawer instanceof AhaDrawer) drawer = newDrawer;
      }
    }

    // skip routing if there are any custom options, since they can't be encoded in the URL
    if (Object.keys(options).length > 0) {
      return drawer._showUrl(url, options);
    }

    const windowUrl = drawer.getWindowUrl(url);
    drawer.router.navigate(windowUrl);
  }

  private _showUrl(url: string | undefined, options: ShowUrlOptions = {}) {
    if (!url) {
      if (this.isOpen()) {
        AssistantContext.instance.setReferences([]);
      }
      this._hide();
      return;
    }

    if (!options.fromAssistant) {
      const referenceNum = getReferenceNumFromUrl(url);
      if (referenceNum) {
        AssistantContext.instance.setReferences([referenceNum]);
      } else {
        AssistantContext.instance.waitForDrawerLoad = true;
      }
    }

    this.loading = true;

    this.deregisterReactiveDestroyUpdates();
    const activeTab = this.activeTabForUrl(url);

    this.activate();
    const previousHistoryItem = this.currentItem();
    const historyItem: HistoryItem = { url, options };
    if (!options.replace) {
      this.history.push(historyItem);
    }
    window.AhaSearchContext && window.AhaSearchContext.record(null);

    // Blur any non-editor element currently being edited so it is saved correctly.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (!this.$el().hasClass('bottom-drawer')) {
      this.setupDrawerResizing();
    }

    if (!options.keepExistingContent) {
      const spinner = this.$spinner();
      spinner && spinner.show();

      unmountComponents(this.$el());

      this.$el().addClass('in');
      this.$el().find('.content-main').html('');
    }

    if (window.featureFlags?.A_18022_add_popover_to_legacy_elements) {
      // If we are using popover, bring the drawer to the top.
      const el = this.$el()[0];
      if (el) {
        if (!el.hasAttribute('popover')) {
          el.setAttribute('popover', 'manual');
        }
        el.hidePopover();
        el.showPopover();
      }
      this.$el()
        .find('.content-main')
        .ready(() => document.dispatchEvent(new CustomEvent('drawer:shown')));
    }

    const fetchFn = options.usePost ? $.post : $.get;

    return fetchFn({
      url,
      data: {
        format: 'drawer',
        show_back: this.history.length > 1,
        active_tab: activeTab,
        ...options.data,
      },
      headers: options.headers,
      success: data => {
        const spinner = this.$spinner();
        spinner && spinner.hide();

        // This is server-rendered HTML.
        // eslint-disable-next-line no-unsanitized/method
        this.$el().find('.content-main').html(data);

        const isTabbedDetails = this.$el().find('.tabbed-drawer').length > 0;

        if (isTabbedDetails) {
          this.registerReactiveDestroyUpdates();
        }

        this.$el()
          .find('> .content')
          .attr('id', isTabbedDetails ? 'tabbed-record-contents' : null);

        // Add title to the history item
        const drawerTitle = this.$el()
          .find('.details__name--row .details__name')
          .text();
        if (drawerTitle) {
          historyItem.title = drawerTitle;
        }

        this.updateDrawerResizingHandleColor();

        if (
          isTabbedDetails &&
          previousHistoryItem &&
          previousHistoryItem.title
        ) {
          this.$el()
            .find('.back-button')
            .attr({
              'data-css-only-tooltip-placement': 'left',
              'data-css-only-tooltip-size': 'one-line-large',
              'data-css-only-tooltip': `Back to "${previousHistoryItem.title}"`,
            });
        }
      },
    }).done(() => {
      if (this.clickedField instanceof HTMLElement) {
        this.showClickedField(this.clickedField, url);
      }

      // set the close button title
      $(`${this.drawerSelector} .drawer-close-button`).prop(
        'title',
        this.closeButtonTitle
      );

      // let everyone know when the content is loaded
      this.$el()
        .find('.content-main')
        .ready(() =>
          document.dispatchEvent(
            new CustomEvent('drawer:load', {
              detail: { drawer: this },
            })
          )
        );

      this.loading = false;
    });
  }

  showClickedField(field: HTMLElement, url: string) {
    const type = url.split('/')[3].slice(0, -1);

    if (this.scrollable(url)) {
      const typeId = url.split('/')[4];
      const fieldId = field.dataset.fieldId;
      const attributeId = `attribute_${type}_${typeId}_${fieldId}`;
      const attributeElem = document.getElementById(attributeId);

      const drawerOffset = $('#tabbed-record-contents').offset()?.top ?? 0;
      const drawerNavOffset = ($('.drawer-nav').offset()?.top ?? 0) + 30;
      const drawerTabsOffset =
        $('.tabbed-record .flush-tabs__container').height() ?? 0;

      const totalOffsetHeight =
        drawerOffset + drawerNavOffset + drawerTabsOffset;
      if (attributeElem) {
        $('.drawer-contents').animate(
          {
            scrollTop:
              ($(attributeElem).offset()?.top ?? 0) - totalOffsetHeight,
          },
          50
        );
      }
    }
  }

  hide() {
    this.showUrl(undefined);
  }

  private _hide() {
    // If this is a custom drawer, when it is closed we should remove it from the page and the DrawerStacker.drawers array.
    if (this.customDrawerId) {
      this.$el().on('transitionend', e => {
        $(e.currentTarget).remove();
      });
    }

    $(document).trigger(`${this.eventPrefix}.hide`);
    this.history = [];
    this.$el().removeClass('in');
    this.deActivate();
    window.AhaSearchContext && window.AhaSearchContext.record(null);

    unmountComponents(this.$el());
    this.deregisterReactiveDestroyUpdates();

    // Clear out drawer contents
    this.$el().find('.content-main').html('');
  }

  isOpen() {
    return this.$el().hasClass('in');
  }

  canOpen() {
    return this.$el().length > 0;
  }

  isTop() {
    if (
      window.featureFlags?.A_18022_add_popover_to_legacy_elements &&
      window.featureFlags?.ACT_1949_multiple_drawers
    ) {
      const stacker = getDrawerStacker();
      if (!stacker) return false;
      return stacker.topDrawer === this;
    } else {
      return String(this.$el().css('zIndex')) === '901';
    }
  }

  isDisabledLink(elem: HTMLElement) {
    return (
      elem.classList.contains('disabled') || elem.getAttribute('data-disabled')
    );
  }

  // Certain contexts have special conditions for opening the drawer. In those
  // cases, data-drawer-disabled is added to prevent the drawer from opening on
  // click.
  drawerDisabled(elem: HTMLElement) {
    return elem.hasAttribute('data-drawer-disabled');
  }

  // Disable the drawer on a subelement of the drawer-url element
  hasDrawerDisabledParent(event: JQuery.ClickEvent | JQuery.DoubleClickEvent) {
    const closestDisabledElement = event.target.closest(
      '[data-drawer-disabled]'
    );
    return !!closestDisabledElement;
  }

  maybeStopLink(event: JQuery.ClickEvent | JQuery.DoubleClickEvent) {
    if (
      this.isDisabledLink(event.currentTarget) ||
      this.drawerDisabled(event.currentTarget) ||
      this.hasDrawerDisabledParent(event)
    ) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
  }

  setToTop() {
    if (
      window?.featureFlags?.A_18022_add_popover_to_legacy_elements &&
      window?.featureFlags?.ACT_1949_multiple_drawers
    ) {
      const el = this.$el()[0];
      // If the drawer is already at the top, we don't need to do anything.
      if (popoverStacker.isTopPopover(el)) return;
      popoverStacker.moveToTop(el);

      // FIXME: Possibly delete, this is handled by the popoverStacker now.
      // // If there is a fullscreened element within the drawer, we need to
      // // hide and show it to ensure it is on top of the drawer.
      // const fullscreenElem = el.querySelector(
      //   ':scope .aha-fullscreen:popover-open'
      // );
      // if (fullscreenElem instanceof HTMLElement) {
      //   popoverStacker.moveToTop(fullscreenElem);
      // }
    } else {
      // Full screen window z-index is 9999, if the full screen is active we
      // need to boost our z-index to be above it
      if (window.location.search.includes('full_screen'))
        this.$el().css('zIndex', 10000);
      else this.$el().css('zIndex', 901);
    }
  }

  setToBottom() {
    if (
      !window?.featureFlags?.A_18022_add_popover_to_legacy_elements &&
      !window?.featureFlags?.ACT_1949_multiple_drawers
    ) {
      this.$el().css('zIndex', 900);
    }
  }

  activate() {
    const stacker = getDrawerStacker();
    if (stacker) stacker.activate(this);
  }

  deActivate() {
    const stacker = getDrawerStacker();
    if (stacker) {
      stacker.deActivate(this);
    }
  }

  elementIsInDrawer($el: JQuery) {
    const el = $el[0];
    const drawerEl = this.$el()[0];
    return el && drawerEl && (el === drawerEl || drawerEl.contains(el));
  }

  deregisterReactiveDestroyUpdates() {
    if (this.reactiveDestroyId) {
      ReactiveRegister.deregister(this.reactiveDestroyId);
      this.reactiveDestroyId = undefined;
    }
  }

  // Register for reactive destroy updates to the record. If the record
  // is destroyed while the drawer is open on that record, close the drawer.
  registerReactiveDestroyUpdates() {
    const tabbedDrawer = this.$el().find('.tabbed-drawer');
    const tabbedDrawerId = tabbedDrawer.attr('id');
    const reactivePattern = tabbedDrawer.attr('data-reactive-record');
    const reactiveId = `${tabbedDrawerId}-close-on-destroy`;
    this.reactiveDestroyId = reactiveId;

    ReactiveRegister.register(
      reactiveId,
      `/${reactivePattern}`,
      (change, ownChanges) => {
        if (ownChanges) {
          return;
        }
        if (
          change.action === 'destroy' &&
          this.reactiveDestroyId === reactiveId
        ) {
          AhaDrawer.goBackOrHide();
          showWarningMessage('The record you were viewing has been deleted.');
        }
      }
    );
  }

  // Allow the user to set the drawer width
  setupDrawerResizing() {
    const localStorageKey = 'drawerWidthPx';

    // When the drawer is hidden we have to reset it back to it's default state.
    // If we don't do this, the sliding effect on the drawer fails and it will
    // be invisible, but still covering elements on the page.
    $(document)
      .off('aha-drawer.hide.resizable-reset')
      .on('aha-drawer.hide.resizable-reset', () => {
        this.$el().css({ width: '', left: '', 'max-width': '' });
      });

    const DEFAULT_DRAWER_WIDTH = '850px';

    // Make the drawer wider by default, users can resize it to be smaller or larger.
    const userSetDrawerWidth =
      localStorage.getItem(localStorageKey) || DEFAULT_DRAWER_WIDTH;

    // Restore the drawer width back to the last saved state if there was one
    if (userSetDrawerWidth) {
      this.$el().css('max-width', '90%');
      this.$el().css('width', userSetDrawerWidth);
    }

    const minWidth = 580;

    this.$el().resizable({
      handles: 'w',
      minWidth: minWidth,
      start: () => {
        this.$el().css('max-width', '90%');
      },
      stop: (event, ui) => {
        // jQuery resizble sets the left and width so the element stays in the
        // same place. When the window is resized, we don't want the drawer to
        // move around, we wan't it stuck to the right side.
        this.$el().css('left', 'initial');

        localStorage.setItem(localStorageKey, ui.size.width);

        // Send a resize event so the editor knows it has to recalculate its
        // navigation
        window.dispatchEvent(new Event('resize'));

        this.trackDrawerResizingAnalytics(ui.originalSize.width, ui.size.width);
      },
      resize: (event, ui) => {
        // Constrain the resizing to be between 90% and 50% of screen width
        const maxWidth = 0.9 * window.innerWidth;
        const maxLeft = 0.1 * window.innerWidth;
        if (ui.size.width > maxWidth) {
          ui.size.width = maxWidth;
        }

        if (ui.position.left < maxLeft) {
          ui.position.left = maxLeft;
        }

        const minWidth = 580;
        const minLeft = window.innerWidth - minWidth;

        if (ui.size.width < minWidth) {
          ui.size.width = minWidth;
        }

        if (ui.position.left > minLeft) {
          ui.position.left = minLeft;
        }
      },
    });

    // Double clicking the resize handle will set it back to its default width
    this.$el()
      .find('.ui-resizable-w')
      .on('dblclick', () => {
        localStorage.removeItem(localStorageKey);
        this.$el().resizable().resizable('disable');
        this.$el().css({ width: '', left: '', 'max-width': '' });
        this.$el().resizable().resizable('enable');
      })
      .attr(
        'title',
        userSetDrawerWidth
          ? 'Double click to reset drawer back to default width'
          : ''
      );
  }

  trackDrawerResizingAnalytics(originalWidth: number, newWidth: number) {
    let recordType: string = '';

    // Grab the record type from the tabbed-drawer data-[x]-id attribute
    $.each(
      this.$el().find('.tabbed-drawer').data(),
      (attribute: string, value) => {
        const recordTypeMatch = attribute.match(/(.*)Id/);
        if (recordTypeMatch) {
          recordType = recordTypeMatch[1];
        }
      }
    );

    window.AhaAnalytics.track(
      'Feature-7125092238292964395',
      'User resizes drawer',
      {
        'Record type': recordType,
        'Previous width': originalWidth,
        'New width': newWidth,
      }
    );
  }

  // The drawer handle color is based on top nav drawer color or defaults to
  // blue 500.
  updateDrawerResizingHandleColor() {
    const drawerNavColor =
      this.$el().find('.drawer-nav').css('border-top-color') ||
      'var(--aha-blue-500)';
    this.$el()
      .find('.ui-resizable-w')
      .css('--drawer-drag-handle-color', drawerNavColor);
  }

  // When deleting a record that was navigated to from within the drawer,
  // go back to the previous record. Otherwise hide the drawer.
  static goBackOrHide() {
    const instance = this.instance();
    if (instance) {
      if (instance.history.length > 1) {
        instance.history.pop();
        const previousItem = instance.history.pop();
        if (!previousItem) return;
        const { url } = previousItem;
        instance.showUrl(url);
      } else {
        instance.showUrl(undefined);
      }
    }
  }

  // Close any open drawer.
  static hide() {
    const instance = this.instance();
    if (instance) {
      return instance.showUrl(undefined);
    }
  }
  static load(element) {
    const instance = this.instance();
    if (instance) {
      return instance.load(element);
    }
  }
  static showUrl(url, options?: ShowUrlOptions) {
    const instance = this.instance();
    if (instance) {
      return instance.showUrl(url, options);
    }
  }
  static isOpen() {
    const instance = this.instance();
    if (instance) {
      return instance.isOpen();
    } else {
      return false;
    }
  }
  static canOpen() {
    const instance = this.instance();
    if (instance) {
      return instance.canOpen();
    } else {
      return false;
    }
  }
  static openRecord(recordRef: RecordReferenceStruct) {
    const instance = this.instance();
    if (instance) {
      return instance.openRecord(recordRef);
    }
  }
  static instance() {
    return getDrawerStacker()?.topSideDrawer;
  }
}

export default AhaDrawer;
